import React, { useState, useEffect } from 'react';
import { budgetQuotationService } from "../../budget/services/budgetQuotationService";
import { orderingQuotationService } from "../../ordering/services/orderingQuotationService";
import { LeadExecutionTable } from './lead_ececution_table';
import { Package, FileText, ShoppingCart, Plus } from "lucide-react";

import { ProjectInventoryTab } from '../../verve-projects-flow-final/components/ProjectInventoryTab';

export const LeadExecutionMaterialsTab = ({ project, tasksInventoryLocked, viewerRole, onUpdate }) => {
  const [budgetItems, setBudgetItems] = useState([]);
  const [orderingItems, setOrderingItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('budget');

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      const leadId = project?.leadId?._id || project?.leadId || project?._id;

      // Fetch Budget Quotations
      const budgetRes = await budgetQuotationService.getQuotations();
      const allBudgetQuotations = budgetRes?.data || [];
      const budgetQuotations = allBudgetQuotations.filter(q => {
        return (q.contactRef && q.contactRef === leadId) || 
               (q.contactObj && q.contactObj._id === leadId);
      });

      // Extract rows from the latest approved budget quotation, or latest if none approved
      let targetBudget = budgetQuotations.find(q => q.status === 'Approved');
      if (!targetBudget && budgetQuotations.length > 0) {
        targetBudget = budgetQuotations[0];
      }
      
      const bItems = [];
      if (targetBudget && targetBudget.rows) {
        targetBudget.rows.forEach(r => {
          bItems.push({
            id: r.id || Math.random(),
            name: r.particulars || r.name || '-',
            design: r.design || '-',
            type: r.type || '-',
            quantity: r.quantity || 1,
            colour: r.colour || r.colourType || '-',
            amount: r.amount || 0
          });
        });
      }
      setBudgetItems(bItems);

      // Fetch Ordering Quotations
      const orderingRes = await orderingQuotationService.getQuotations();
      const allOrderingQuotations = orderingRes?.data || [];
      const orderingQuotations = allOrderingQuotations.filter(q => {
        return (q.contactRef && q.contactRef === leadId) || 
               (q.contactObj && q.contactObj._id === leadId);
      });

      // Extract rows from latest ordering quotation
      let targetOrdering = orderingQuotations.find(q => q.orderStatus === 'Placed' || q.status === 'Approved');
      if (!targetOrdering && orderingQuotations.length > 0) {
        targetOrdering = orderingQuotations[0];
      }

      const oItems = [];
      if (targetOrdering && targetOrdering.rows) {
        targetOrdering.rows.forEach(r => {
          oItems.push({
            id: r.id || Math.random(),
            name: r.particulars || r.name || '-',
            design: r.design || '-',
            type: r.type || '-',
            quantity: r.quantity || 1,
            colour: r.colour || r.colourType || '-',
            amount: r.amount || 0
          });
        });
      }
      setOrderingItems(oItems);

    } catch (err) {
      console.error("Error fetching materials items:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      fetchItems();
    }
  }, [project]);

  const columns = [
    {
      header: "Particulars / Item",
      render: (record) => (
        <span className="font-semibold text-[#65768e] text-[11px] uppercase tracking-wider">{record.name}</span>
      )
    },
    {
      header: "Design",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">{record.design}</span>
      )
    },
    {
      header: "Type",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">{record.type}</span>
      )
    },
    {
      header: "Quantity",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">{record.quantity}</span>
      )
    },
    {
      header: "Colour",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">{record.colour}</span>
      )
    },
    {
      header: "Amount",
      render: (record) => (
        <span className="text-[#65768e] text-[11px] font-semibold">
          ₹ {Number(record.amount || 0).toLocaleString('en-IN')}
        </span>
      )
    }
  ];

  return (
    <div className="flex flex-col animate-in fade-in duration-500 bg-white border border-slate-200 mt-2 rounded-xl overflow-hidden shadow-sm">
      
      {/* Sub-tabs for Budget and Ordering Items */}
      <div className="flex items-center gap-2 border-b border-slate-200 px-2 pt-2 bg-slate-50">
        <button
          type="button"
          onClick={() => setActiveSubTab('budget')}
          className={`flex items-center gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors border-b-2 ${
            activeSubTab === 'budget' 
              ? 'border-[#2b448f] text-[#2b448f]' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <FileText className="w-3.5 h-3.5" />
          Budget Items ({budgetItems.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('ordering')}
          className={`flex items-center gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors border-b-2 ${
            activeSubTab === 'ordering' 
              ? 'border-[#2b448f] text-[#2b448f]' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          Ordering Items ({orderingItems.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('request')}
          className={`flex items-center gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors border-b-2 ${
            activeSubTab === 'request' 
              ? 'border-[#2b448f] text-[#2b448f]' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Plus className="w-3.5 h-3.5" />
          Request Material
        </button>
      </div>

      <div className="flex flex-col w-full">
        {activeSubTab !== 'request' ? (
          <>
            <LeadExecutionTable 
              columns={columns} 
              data={activeSubTab === 'budget' ? budgetItems : orderingItems} 
              isLoading={isLoading} 
            />
            {(activeSubTab === 'budget' ? budgetItems : orderingItems).length === 0 && !isLoading && (
              <div className="flex items-center justify-center py-8 text-[12px] text-slate-400">
                No items found for {activeSubTab === 'budget' ? 'Budget' : 'Ordering'}.
              </div>
            )}
          </>
        ) : (
          <div className="p-2">
             <ProjectInventoryTab
                project={project}
                tasksInventoryLocked={tasksInventoryLocked}
                viewerRole={viewerRole}
                hideHeader={true}
                onUpdate={onUpdate}
             />
          </div>
        )}
      </div>

    </div>
  );
};
